import React, { useEffect, useState } from 'react';
import { usePIM } from '../../context/PIMContext';
import { LayoutGrid, AlertCircle, FileText, Languages, Image, Box, CheckCircle2, Clock, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { LiveTrackingMap } from '../widgets/LiveTrackingMap';

const SalesDashboard = () => {
    const { fetchDashboardOverview, statuses: masterStatuses, fetchProduct } = usePIM();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeFilters, setActiveFilters] = useState(['MAIN']); // Array for multi-select, default MAIN enabled globally
    const [activeStatusFilters, setActiveStatusFilters] = useState([]); 
    const [metricFilter, setMetricFilter] = useState(null); // null, 'active', 'completed'
    const [currentTab, setCurrentTab] = useState('project'); // 'project' | 'transport'
    const [inactiveShippingProducts, setInactiveShippingProducts] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        const load = async () => {
            const data = await fetchDashboardOverview();
            setProjects(data);
            setLoading(false);
        };
        load();
    }, [fetchDashboardOverview]);

    const calculateProgress = (p) => {
        let score = 0;
        let total = 4; // 4 Milestones

        // Milestone 1: Created
        if (p.id) score++;

        // Milestone 2: Charge Active
        if (p.active_charge_id) score++;

        // Milestone 3: Content (Properties/Languages)
        if (p.property_count > 0 && p.language_count > 0) score++;

        // Milestone 4: Assets (Images/Packaging)
        if (p.picture_count > 0 && p.packaging_count > 0) score++;

        return Math.round((score / total) * 100);
    };

    const getStatusStyle = (statusName) => {
        const s = masterStatuses.find(ms => ms.name === statusName);
        const color = s?.hex_code || '#78716c';
        return {
            backgroundColor: `${color}20`,
            color: color,
            borderColor: `${color}50`
        };
    };

    const handleTileClick = async (sku) => {
        await fetchProduct(sku);
        navigate('/');
    };

    if (loading) return <div className="min-h-screen bg-stone-950 flex items-center justify-center text-stone-500">Loading Dashboard...</div>;

    // Category Mapping for Filters
    const CATEGORY_MAPPING = {
        'PACKAGING': ['PACKAGING', 'SALES PACKAGE', 'QSG', 'BOX', 'MANUAL'],
        'ACCESSORY': ['ACCESSORY', 'CABLE', 'ADAPTER', 'HEADSET'],
        'COMPONENT': ['COMPONENT', 'BATTERY', 'SCREEN', 'PCB'],
        'SPARE PART': ['SPARE PART', 'SCREW', 'FOIL']
    };

    // Filter Logic
    const filteredProjects = projects.filter(p => {
        // 1. Status Filter (Multi-select)
        if (activeStatusFilters.length > 0) {
            if (!activeStatusFilters.includes(p.product_status)) return false;
        }

        // Always hide "Delete" or "Deleted" even if not filtered explicitly (Safety)
        if (['Delete', 'Deleted'].includes(p.product_status)) return false;

        // Normalize category for comparison
        const category = p.category ? p.category.toUpperCase() : '';

        // 2. Type Filter (Multi-Select)
        if (activeFilters.length > 0) {
            // If any filter is active, the product MUST match at least one
            const matchesAnyFilter = activeFilters.some(filter => {
                if (filter === 'MAIN') {
                    const types = p.product_types ? (typeof p.product_types === 'string' ? JSON.parse(p.product_types) : p.product_types) : [];
                    return types.includes('main');
                } else {
                    const allowedCategories = CATEGORY_MAPPING[filter] || [filter];
                    return allowedCategories.includes(category);
                }
            });
            if (!matchesAnyFilter) return false;
        } else {
            // Default View (No filters): Hide special categories unless they are ALSO "main"
            const allSpecialCategories = Object.values(CATEGORY_MAPPING).flat();
            if (allSpecialCategories.includes(category)) {
                const types = p.product_types ? (typeof p.product_types === 'string' ? JSON.parse(p.product_types) : p.product_types) : [];
                if (!types.includes('main')) return false;
            }
        }

        // 3. Metric Filter
        if (metricFilter === 'active' && p.product_status !== 'Active') return false;
        if (metricFilter === 'completed' && calculateProgress(p) !== 100) return false;

        return true;
    });

    // Metrics based on FILTERED view
    const totalProjects = filteredProjects.length;
    const activeProjects = filteredProjects.filter(p => p.product_status === 'Active').length;
    const completedProjects = filteredProjects.filter(p => calculateProgress(p) === 100).length;

    return (
        <div className="min-h-screen bg-stone-950 text-stone-200 font-sans selection:bg-orange-500/30 p-8">
            {/* Header */}
            <header className="flex justify-between items-center mb-10">
                <div className="flex gap-8 items-center">
                    <div>
                        <h1 className="text-3xl font-normal bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Project Overview</h1>
                        <p className="text-stone-500 mt-1">Real-time status of all PIM models</p>
                    </div>

                    {/* Tabs */}
                    <div className="flex bg-stone-900/50 p-1 rounded-lg border border-stone-800 ml-4">
                        <button
                            onClick={() => setCurrentTab('project')}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${currentTab === 'project' ? 'bg-stone-800 text-white shadow-sm' : 'text-stone-500 hover:text-stone-300'}`}
                        >
                            <LayoutGrid size={16} /> Overview
                        </button>
                        <button
                            onClick={() => setCurrentTab('transport')}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${currentTab === 'transport' ? 'bg-stone-800 text-white shadow-sm' : 'text-stone-500 hover:text-stone-300'}`}
                        >
                            <Globe size={16} /> Transport Overview
                        </button>
                    </div>
                </div>

                {/* Filters */}
                <div className="flex gap-2">
                    <div className="flex flex-wrap gap-2">
                        {['MAIN', 'ACCESSORY', 'COMPONENT', 'SPARE PART', 'PACKAGING'].map(type => (
                            <button
                                key={type}
                                onClick={() => {
                                    setActiveFilters(prev => 
                                        prev.includes(type) 
                                            ? prev.filter(t => t !== type) 
                                            : [...prev, type]
                                    );
                                }}
                                className={`px-2 py-1.5 rounded border transition-all cursor-pointer select-none text-[10px] font-normal uppercase ${activeFilters.includes(type)
                                    ? 'bg-blue-900/30 border-blue-600 text-blue-200 shadow-lg shadow-blue-900/20'
                                    : 'bg-stone-950 border-stone-700 text-stone-400 hover:bg-stone-900'
                                    }`}
                            >
                                {type}
                            </button>
                        ))}
                    </div>
                </div>

                <button
                    onClick={() => navigate('/')}
                    className="flex items-center gap-2 px-4 py-2 rounded-full border border-stone-700 hover:bg-stone-800 transition text-stone-400 hover:text-white"
                >
                    <LayoutGrid size={16} /> Open Editor
                </button>
            </header>

            {/* Tab Content: Project Overview */}
            {currentTab === 'project' && (
                <>
                    {/* Status Filters (Multi-Select) */}
                    <div className="mb-8">
                        <div className="text-[10px] font-medium text-stone-500 uppercase mb-2 tracking-wider">Project Status</div>
                        <div className="flex flex-wrap gap-2">
                            {['Draft', 'Development', 'In Production', 'Produced', 'Shipped'].map(status => {
                                const isActive = activeStatusFilters.includes(status);
                                return (
                                    <button
                                        key={status}
                                        onClick={() => {
                                            setActiveStatusFilters(prev =>
                                                prev.includes(status)
                                                    ? prev.filter(s => s !== status)
                                                    : [...prev, status]
                                            );
                                        }}
                                        className={`px-2 py-1.5 rounded border transition-all cursor-pointer select-none text-[10px] font-normal uppercase ${isActive
                                            ? 'bg-blue-900/30 border-blue-600 text-blue-200 shadow-lg shadow-blue-900/20'
                                            : 'bg-stone-950 border-stone-700 text-stone-400 hover:bg-stone-900'
                                            }`}
                                    >
                                        {status}
                                    </button>
                                );
                            })}
                            {/* Clear Filter Button */}
                            {activeStatusFilters.length > 0 && (
                                <button
                                    onClick={() => setActiveStatusFilters([])}
                                    className="px-3 py-1 text-xs text-stone-500 hover:text-white underline decoration-stone-700 hover:decoration-white transition-all"
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Metrics */}
                    <div className="grid grid-cols-4 gap-6 mb-12">
                        <div 
                            onClick={() => setMetricFilter(null)}
                            className={`bg-stone-900/50 border rounded-2xl p-6 backdrop-blur-sm cursor-pointer transition-all ${metricFilter === null ? 'border-blue-500 ring-1 ring-blue-500/50' : 'border-stone-800 hover:border-stone-600'}`}
                        >
                            <div className="text-stone-500 text-[10px] font-medium uppercase tracking-wider mb-2">Total Projects</div>
                            <div className="text-4xl font-normal text-white">{totalProjects}</div>
                        </div>
                        <div 
                            onClick={() => setMetricFilter('active')}
                            className={`bg-stone-900/50 border rounded-2xl p-6 backdrop-blur-sm cursor-pointer transition-all ${metricFilter === 'active' ? 'border-blue-500 ring-1 ring-blue-500/50' : 'border-stone-800 hover:border-stone-600'}`}
                        >
                            <div className="text-stone-500 text-[10px] font-medium uppercase tracking-wider mb-2">Active</div>
                            <div className="text-4xl font-normal text-blue-400">{activeProjects}</div>
                        </div>
                        <div 
                            onClick={() => setMetricFilter('completed')}
                            className={`bg-stone-900/50 border rounded-2xl p-6 backdrop-blur-sm cursor-pointer transition-all ${metricFilter === 'completed' ? 'border-green-500 ring-1 ring-green-500/50' : 'border-stone-800 hover:border-stone-600'}`}
                        >
                            <div className="text-stone-500 text-[10px] font-medium uppercase tracking-wider mb-2">Completed</div>
                            <div className="text-4xl font-normal text-green-400">{completedProjects}</div>
                        </div>
                        <div className="bg-stone-900/50 border border-stone-800 rounded-2xl p-6 backdrop-blur-sm">
                            <div className="text-stone-500 text-[10px] font-medium uppercase tracking-wider mb-2">Avg. Completion</div>
                            <div className="text-4xl font-normal text-purple-400">
                                {Math.round(filteredProjects.reduce((acc, p) => acc + calculateProgress(p), 0) / (totalProjects || 1))}%
                            </div>
                        </div>
                    </div>

                    {/* Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {filteredProjects.map(project => {
                            const progress = calculateProgress(project);
                            return (
                                <div
                                    key={project.id}
                                    onClick={() => handleTileClick(project.sku)}
                                    className="group relative bg-[#1c1917] border border-stone-800 rounded-2xl overflow-hidden hover:border-stone-600 transition-all duration-300 hover:shadow-2xl hover:shadow-black/50 hover:-translate-y-1 cursor-pointer"
                                >
                                    {/* Hero Image / Fallback */}
                                    <div className="h-40 bg-stone-900 relative overflow-hidden flex items-center justify-center p-4">
                                        {project.main_image || project.image_url ? (
                                            <img src={project.main_image || project.image_url} alt={project.model_name} className="max-w-full max-h-full object-contain opacity-90 group-hover:opacity-100 transition-opacity" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-stone-700">
                                                <Image size={48} />
                                            </div>
                                        )}
                                        <div 
                                            className={`absolute top-4 right-4 px-3 py-1 rounded-full text-[10px] font-medium border backdrop-blur-md`}
                                            style={getStatusStyle(project.product_status)}
                                        >
                                            {project.product_status}
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div className="p-6">
                                        <div className="flex justify-between items-start mb-4">
                                            <div>
                                                <div className="text-xs text-stone-500 font-mono mb-1">{project.sku}</div>
                                                <h3 className="text-xl font-normal text-white group-hover:text-blue-400 transition-colors">{project.model_name}</h3>
                                                <div className="text-xs text-stone-600 mt-1">{project.category || 'Uncategorized'}</div>
                                            </div>
                                        </div>

                                        {/* Progress Bar */}
                                        <div className="mb-6">
                                            <div className="flex justify-between items-end mb-2">
                                                <span className="text-xs text-stone-400">Progress</span>
                                                <span className="text-xs font-bold text-white">{progress}%</span>
                                            </div>
                                            <div className="h-2 w-full bg-stone-800 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full bg-gradient-to-r from-blue-600 to-purple-600 transition-all duration-1000"
                                                    style={{ width: `${progress}%` }}
                                                />
                                            </div>
                                        </div>

                                        {/* Stats Grid */}
                                        <div className="grid grid-cols-2 gap-2">
                                            <div className="flex items-center gap-2 text-xs text-stone-400 bg-stone-900/50 p-2 rounded border border-stone-800/50">
                                                <FileText size={12} className={project.property_count > 0 ? "text-green-500" : "text-stone-600"} />
                                                <span>{project.property_count} Props</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-xs text-stone-400 bg-stone-900/50 p-2 rounded border border-stone-800/50">
                                                <Languages size={12} className={project.language_count > 0 ? "text-green-500" : "text-stone-600"} />
                                                <span>{project.language_count} Langs</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-xs text-stone-400 bg-stone-900/50 p-2 rounded border border-stone-800/50">
                                                <Box size={12} className={project.packaging_count > 0 ? "text-green-500" : "text-stone-600"} />
                                                <span>{project.packaging_count} Pack</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-xs text-stone-400 bg-stone-900/50 p-2 rounded border border-stone-800/50">
                                                <Image size={12} className={project.picture_count > 0 ? "text-green-500" : "text-stone-600"} />
                                                <span>{project.picture_count} Pics</span>
                                            </div>
                                        </div>

                                        {/* Active Charge Info */}
                                        <div className="mt-4 pt-4 border-t border-stone-800 flex items-center justify-between text-xs text-stone-500">
                                            <div className="flex items-center gap-1.5">
                                                <Clock size={12} />
                                                <span>Active Charge:</span>
                                            </div>
                                            <span className={`font-mono ${project.active_charge_id ? 'text-stone-300' : 'text-red-900'}`}>
                                                {project.active_charge_id || 'None'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            {/* Tab Content: Transport Overview */}
            {currentTab === 'transport' && (() => {
                const shippingProducts = filteredProjects.filter(p => p.product_status === 'Shipped' || p.product_status === 'Produced' || p.product_status === 'Active').map(project => {
                    let etaFormatted = "TBA";
                    const dateOptions = { year: 'numeric', month: 'long', day: 'numeric' };
                    if (project.tracking_eta) {
                        etaFormatted = new Date(project.tracking_eta).toLocaleDateString('en-US', dateOptions);
                    } else if (project.manual_eta) {
                        etaFormatted = new Date(project.manual_eta).toLocaleDateString('en-US', dateOptions);
                    } else {
                        const mockDays = 7 + (project.id % 14);
                        const etaDate = new Date();
                        etaDate.setDate(etaDate.getDate() + mockDays);
                        etaFormatted = "~" + etaDate.toLocaleDateString('en-US', dateOptions);
                    }
                    
                    const destinationPort = project.destination_port || "Hannover";
                    
                    return { ...project, etaFormatted, destinationPort };
                });

                const visibleProducts = shippingProducts.filter(p => !inactiveShippingProducts.includes(p.id));
                
                return (
                <div className="flex h-[calc(100vh-160px)] gap-6">
                    <div className="flex-[2] bg-stone-900/50 border border-stone-800 rounded-2xl overflow-hidden relative">
                        <LiveTrackingMap products={visibleProducts} />
                    </div>
                    
                    <div className="flex-1 bg-stone-900/30 border border-stone-800 rounded-2xl p-4 flex flex-col overflow-hidden">
                        <h2 className="text-xl font-bold text-white mb-4">Shipping Units</h2>
                        
                        <div className="overflow-y-auto flex-1 space-y-2 pr-2">
                            {shippingProducts.map(project => {
                                    const isSelected = !inactiveShippingProducts.includes(project.id);

                                    return (
                                        <div 
                                            key={project.id}
                                            onClick={() => setInactiveShippingProducts(prev => prev.includes(project.id) ? prev.filter(id => id !== project.id) : [...prev, project.id])}
                                            className={`p-3 rounded-xl border transition-all cursor-pointer select-none flex items-center gap-3 ${isSelected ? 'bg-blue-900/20 border-blue-500/50 shadow-inner' : 'bg-stone-950 border-stone-800 opacity-60 hover:opacity-80'}`}
                                        >
                                            <div className="w-12 h-12 bg-stone-900 rounded-lg flex items-center justify-center overflow-hidden shrink-0 border border-stone-800">
                                                 {project.main_image || project.image_url ? (
                                                    <img src={project.main_image || project.image_url} alt={project.model_name} className="max-w-full max-h-full object-contain" />
                                                ) : <Image size={24} className="text-stone-700" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium text-white truncate">{project.model_name}</div>
                                                <div className="text-xs text-stone-500 font-mono truncate">{project.sku}</div>
                                            </div>
                                            <div className="text-right shrink-0">
                                                <div className="text-[10px] text-stone-500 uppercase tracking-wider mb-0.5">ETA • {project.destinationPort}</div>
                                                <div className={`text-sm font-mono font-medium ${isSelected ? 'text-blue-400' : 'text-stone-500'}`}>{project.etaFormatted}</div>
                                            </div>
                                        </div>
                                    );
                                })
                            }
                            {filteredProjects.filter(p => p.product_status === 'Shipped' || p.product_status === 'Produced' || p.product_status === 'Active').length === 0 && (
                                <div className="text-stone-500 text-sm text-center py-8">No matching shipping units found.</div>
                            )}
                        </div>
                    </div>
                </div>
                );
            })()}
        </div>
    );
};

export default SalesDashboard;
